import { create } from "zustand";
import { subscribeToMediaStream } from "../api/stream";
import { castKeyOf, isCastLocalId } from "../lib/castDock";
import { slotEntries } from "../lib/streamSlot";
import logger from "../lib/logger";
import { useStreamStatusStore } from "./streamStatusStore";

export interface CastDockState {
  /** Watched casts the user hid, by castKeyOf; local only (not a stop). */
  hidden: Record<string, true>;
  /** When this tab first saw each cast, by castKeyOf: the dock's start order. */
  seen: Record<string, number>;
  /** Casts this tab told main-api it watches (Android subscribeToVMPLSlot), by castKeyOf. */
  subscribed: Record<string, true>;
  hide: (key: string) => void;
  show: (key: string) => void;
  reset: () => void;
}

const initial = { hidden: {}, seen: {}, subscribed: {} } as Pick<CastDockState, "hidden" | "seen" | "subscribed">;

export const useCastDockStore = create<CastDockState>((set) => ({
  ...initial,
  hide: (key) => set((s) => ({ hidden: { ...s.hidden, [key]: true } })),
  show: (key) =>
    set((s) => {
      if (!s.hidden[key]) return s;
      const hidden = { ...s.hidden };
      delete hidden[key];
      return { hidden };
    }),
  reset: () => set({ ...initial }),
}));

/** Every cast the status store reports busy gets a first-seen time; the open GC's are subscribed once. */
useStreamStatusStore.subscribe((status) => {
  const dock = useCastDockStore.getState();
  let seen = dock.seen;
  let subscribed = dock.subscribed;
  let hidden = dock.hidden;
  const now = Date.now();
  for (const [gcKey, info] of Object.entries(status.infoByGc)) {
    for (const { localId, slot } of slotEntries(info)) {
      if (!isCastLocalId(localId)) continue;
      if (slot.available === true) {
        // A freed slot: the next cast there starts with fresh dock state.
        const prefix = `${slot.id}:`;
        const drop = <T,>(m: Record<string, T>): Record<string, T> =>
          Object.keys(m).some((k) => k.startsWith(prefix))
            ? Object.fromEntries(Object.entries(m).filter(([k]) => !k.startsWith(prefix)))
            : m;
        seen = drop(seen);
        subscribed = drop(subscribed);
        hidden = drop(hidden);
        continue;
      }
      if (slot.available !== false) continue;
      const key = castKeyOf(slot);
      if (seen[key] == null) seen = { ...seen, [key]: now };
      if (Number(gcKey) === status.gcId && !subscribed[key]) {
        subscribed = { ...subscribed, [key]: true };
        subscribeToMediaStream(slot.id).catch((e) => logger.warn("[CastDock] subscribe failed", slot.id, e));
      }
    }
  }
  if (seen !== dock.seen || subscribed !== dock.subscribed || hidden !== dock.hidden)
    useCastDockStore.setState({ seen, subscribed, hidden });
});

export function resetCastDockStore(): void {
  useCastDockStore.getState().reset();
}
